import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus, Upload, FileText, Pencil, Trash2 } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function InvoicesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ invoice_number: "", vendor_name: "", amount: 0, currency: "USD", invoice_date: "", company_id: "", notes: "" });
  const fileInputs = useRef({});

  const load = async () => { const { data } = await api.get("/invoices"); setItems(data); };
  useEffect(() => {
    load();
    api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
    api.get("/companies").then(r => setCompanies(r.data));
  }, []);

  const create = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (user.role !== "super_admin") delete payload.company_id;
      if (editing) {
        await api.put(`/invoices/${editing.id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", payload);
        toast.success("Invoice added");
      }
      setOpen(false); setEditing(null); load();
      setForm({ invoice_number: "", vendor_name: "", amount: 0, currency: "USD", invoice_date: "", company_id: "", notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (i) => {
    setEditing(i);
    setForm({
      invoice_number: i.invoice_number, vendor_name: i.vendor_name || "",
      amount: i.amount, currency: i.currency, invoice_date: i.invoice_date || "",
      company_id: i.company_id, notes: i.notes || "",
    });
    setOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/invoices/${deleting.id}`);
      toast.success("Invoice deleted");
      setDeleting(null); load();
    } catch (e) { toast.error("Failed"); }
  };

  const upload = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/invoices/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }});
    toast.success("Uploaded"); load();
  };

  const canEdit = ["super_admin","company_admin","it_admin"].includes(user?.role);

  return (
    <div className="p-8 space-y-6" data-testid="invoices-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Module 04</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Link invoices to vendors, assets, and subscriptions.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <ExportMenu module="invoices" />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="new-invoice-btn" onClick={() => { setEditing(null); setForm({ invoice_number: "", vendor_name: "", amount: 0, currency: "USD", invoice_date: "", company_id: "", notes: "" }); }} className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Plus size={14} className="mr-1"/>New Invoice</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit Invoice" : "Add Invoice"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {user.role === "super_admin" && (
                  <div className="col-span-2"><Label className="data-label">Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                      <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label className="data-label">Invoice #</Label><Input data-testid="invoice-form-number" value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})}/></div>
                <div><Label className="data-label">Date</Label><Input type="date" value={form.invoice_date} onChange={e => setForm({...form, invoice_date: e.target.value})}/></div>
                <div className="col-span-2"><Label className="data-label">Vendor</Label><Input value={form.vendor_name} onChange={e => setForm({...form, vendor_name: e.target.value})} placeholder="Vendor name"/></div>
                <div><Label className="data-label">Amount</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}/></div>
                <div><Label className="data-label">Currency</Label><Input value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}/></div>
                <div className="col-span-2"><Label className="data-label">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
              </div>
              <DialogFooter><Button data-testid="invoice-form-submit" onClick={create} className="bg-black text-white hover:bg-[#FF5000]">{editing ? "Save" : "Create"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <ConfirmDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete invoice?" description={deleting?.invoice_number} confirmLabel="Delete" variant="destructive"
        onConfirm={confirmDelete} testid="delete-invoice" />

      <div className="border border-border bg-white overflow-x-auto" data-testid="invoice-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted">
            {["INV #","VENDOR","AMOUNT","DATE","FILE",""].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-b border-border hover-row" data-testid={`invoice-row-${i.id}`}>
                <td className="p-3 font-mono text-xs">{i.invoice_number}</td>
                <td className="p-3">{i.vendor_name}</td>
                <td className="p-3 font-mono text-xs">{i.currency} {i.amount.toFixed(2)}</td>
                <td className="p-3 font-mono text-xs">{fmtDate(i.invoice_date)}</td>
                <td className="p-3 text-xs">
                  {i.file_url ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${i.file_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#FF5000]"><FileText size={12}/>{i.file_name}</a>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <input ref={el => fileInputs.current[i.id] = el} type="file" className="hidden"
                           onChange={(e) => e.target.files[0] && upload(i.id, e.target.files[0])}/>
                    <button onClick={() => fileInputs.current[i.id]?.click()} className="hover:text-[#FF5000]" title="Upload file"><Upload size={14}/></button>
                    {canEdit && <button data-testid={`edit-invoice-${i.id}`} onClick={() => startEdit(i)} title="Edit" className="hover:text-[#FF5000]"><Pencil size={14}/></button>}
                    {canEdit && <button data-testid={`delete-invoice-${i.id}`} onClick={() => setDeleting(i)} title="Delete" className="hover:text-[#FF3B30]"><Trash2 size={14}/></button>}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No invoices.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
